import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Dialog, DialogTitle, DialogContent, IconButton, Menu, MenuItem, DialogActions, Snackbar, Alert } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { makeStyles } from "@material-ui/core/styles";
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import Grid from "@material-ui/core/Grid";
import Skeleton from "@mui/material/Skeleton";
import {
  getMissionsForClaim,
  createInspectionMission,
} from "../../store/action/inspectionMission";
import getDateFromString from "../../utils/getDateFromString";
import { getLabelClass, renderInputOrSpan } from "../../utils/formUtils";
import { toggleExpansion } from "../../utils/toggleExpansion";
import { traduireTypeDeMission } from "../../utils/translateMissionType";
import { getPersonDetails } from "../../store/action/party";
import { updateClaimDaaq } from "../../store/action/claim";
import ExpertSelector from "../TaskListSection/DynamicComponentTodoList/ExpertSelection";
import axiosInstance from "../../utils/axiosConfig";
import { hasAuthorities } from "../../utils/authoritiesUtils";

const BRAND = {
  blue: "rgb(60, 101, 86)",
  green: "rgba(187, 200, 8, 1)",
  greenLight: "rgba(240, 243, 199, 1)",
  modalBg: "rgb(243, 246, 249)",
};

const LS_KEY = (uidOrNumber) => `missions:${uidOrNumber || "unknown"}`;
const BASE = process.env.REACT_APP_BACKEND_URL_MS_INSPECTION;

async function apiDeleteMission(mission) {
  const id = mission?.uid || mission?._id || mission?.code;
  if (!id) throw new Error("Missing mission id/uid/code");
  await axiosInstance.delete(
    `${BASE}/inspectionMission/${encodeURIComponent(id)}`
  );
}

const MISSION_TYPES = [
  { code: "FIRST_INSPECTION", label: "Demande première expertise" },
  { code: "SECOND_INSPECTION", label: "Demande deuxième expertise" },
  { code: "ARBITRATION", label: "Arbitrage" },
  { code: "COUNTER_INSPECTION", label: "Contre-Expertise" },
  { code: "CARRENCE_REPORT", label: "Rapport de carence" },
  { code: "JUDICIAL_INSPECTION", label: "Expertise judiciaire" },
];

function useNamesMap() {
  const [map, setMap] = useState({});
  // Déjà demandés : une ref, pas un state, pour qu'ensure garde une identité stable.
  // Le lire depuis `map` faisait changer ensure à chaque nom résolu, ce qui relançait
  // les effets qui en dépendent et rechargeait la liste des missions.
  const requested = useRef({});

  const ensure = useCallback(async (val) => {
    if (!val || requested.current[val]) return;
    requested.current[val] = true;

    if (/\s/.test(val) && !/^P[-_]/i.test(val)) {
      setMap((m) => (m[val] ? m : { ...m, [val]: val }));
      return;
    }
    try {
      const p = await getPersonDetails(val);
      const full = `${p?.name || ""} ${p?.lastName || ""}`.trim();
      setMap((m) => ({ ...m, [val]: full || val }));
    } catch {
      setMap((m) => ({ ...m, [val]: val }));
    }
  }, []);

  return { names: map, ensure };
}

function deriveDisplayRegion(person, inspector) {
  const fromPerson =
    person?.address?.region?.name ||
    person?.address?.regionName ||
    person?.region ||
    person?.city ||
    person?.governorate;
  const fromCarac =
    inspector?.inspectorCaracteristiques?.find(
      (c) => c?.key?.toUpperCase() === "REGION"
    )?.value ||
    inspector?.inspectorCaracteristiques?.find(
      (c) => c?.key?.toUpperCase() === "GOUVERNORAT"
    )?.value;
  return fromPerson || fromCarac || "";
}

function deriveDisplayAddress(person, inspector) {
  const fromPerson =
    person?.address?.fullAddress ||
    person?.address?.addressLine ||
    person?.address?.street ||
    person?.address?.line1 ||
    person?.address?.formattedAddress;
  const fromCarac =
    inspector?.inspectorCaracteristiques?.find(
      (c) =>
        c?.key?.toUpperCase() === "ADRESSE" ||
        c?.key?.toUpperCase() === "ADDRESS"
    )?.value;
  return fromPerson || fromCarac || "";
}

function enrichInspector(ins) {
  if (!ins) return ins;
  const p = ins.personDetails || {};
  const displayRegion = deriveDisplayRegion(p, ins);
  const displayAddress = deriveDisplayAddress(p, ins);
  return {
    ...ins,
    personDetails: {
      ...p,
      displayRegion: displayRegion || "",
      displayAddress: displayAddress || "",
    },
  };
}


const useStyles = makeStyles((theme) => ({
  labelInput: {
    fontFamily: "Poppins",
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "18px",
    textAlign: "left",
    color: "rgba(70, 78, 95, 1)",
    marginBottom : '10px'
  },
  styleInput: {
    borderRadius: "4px",
    border : 0,
    background: "rgba(243, 246, 249, 1)",
    height: "36px",
    width: "250px",
    padding: "10px",
    fontFamily: "Poppins",
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "18px",
    textAlign: "left",
    color: "rgba(70, 78, 95, 1)",
  },
  styleInput2: {
    borderRadius: "4px",
    border : 0,
    background: "rgba(243, 246, 249, 1)",
    
    padding: "10px",
    fontFamily: "Poppins",
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "18px",
    textAlign: "left",
    color: "rgba(187, 200, 8, 1)",
  },
  paper: {
    width: "100%",
    boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.06)",
  },
  open: {
    border: "1px solid red",
  } , 
  customAccordionItem : {
    width: "100%",
    marginBottom: "20px",
    borderRadius: "11px",
    border:`1px solid rgba(187, 200, 8, 1)`,
      // "&_expanded" : {
      // border: "1px solid rgba(187, 200, 8, 1)",
      // },
    },
  styleSpan:{
    color: 'rgba(70, 78, 95, 1)',
    fontSize: '12px',
    fontWeight: 400,
    fontFamily: 'Poppins'
  },
  styleSpanLabel:{
    color: 'rgba(70, 78, 95, 1)',
    fontSize: '12px',
    fontWeight: 600,
    fontFamily: 'Poppins'
  },
  expanded: {
    border: "none",
  },
}));

function ExpertisesSection(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const claimNumber = props.claimNumber;
  const relatedClaimUid = props.relatedClaimUid || "";
  const designatedRepairerPartyUid = props.designatedRepairerPartyUid;

  const [loading, setLoading] = useState(true);
  const [expertisesData, setExpertisesData] = useState([]);
  const renderAsSpans = props.renderAsSpans;
  const [expandedItems, setExpandedItems] = useState(new Set([0]));
  const { names, ensure } = useNamesMap();

  const [anchorEl, setAnchorEl] = useState(null);
  const [showInspectorsModal, setShowInspectorsModal] = useState(false);
  const [selectedInspector, setSelectedInspector] = useState(null);
  const [selectedMissionTypeCode, setSelectedMissionTypeCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", msg: "" });

  const { claimData } = useSelector((state) => state.claimReducer || { claimData: [] });
  const currentClaim = useMemo(() => {
    if (!Array.isArray(claimData)) return null;
    return (
      claimData.find((c) => (c?.uid || c?._id) === relatedClaimUid) ||
      claimData.find((c) => c?.claimNumber === claimNumber) ||
      null
    );
  }, [claimData, relatedClaimUid, claimNumber]);
  const agencyName = currentClaim?.reportingAgency?.label || "";
  const cacheKey = LS_KEY(relatedClaimUid || claimNumber);

  const readCache = useCallback(() => {
    try {
      const raw = localStorage.getItem(cacheKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }, [cacheKey]);

  const writeCache = useCallback(
    (list) => {
      try {
        localStorage.setItem(cacheKey, JSON.stringify(list || []));
      } catch {}
    },
    [cacheKey]
  );

  const clearCache = useCallback(() => {
    try {
      localStorage.removeItem(cacheKey);
    } catch {}
  }, [cacheKey]);

  const fetchClaimAssociatedMissions = useCallback(async () => {
    setLoading(true);
    try {
      const list = await dispatch(
        getMissionsForClaim({ relatedClaimUid, claimNumber })
      );
      const safe = Array.isArray(list) ? list : [];
      setExpertisesData(safe);
      writeCache(safe);
      safe.forEach((m) => m?.assignedExpert && ensure(m.assignedExpert));
    } finally {
      setLoading(false);
    }
  }, [dispatch, relatedClaimUid, claimNumber, writeCache, ensure]);

  useEffect(() => {
    fetchClaimAssociatedMissions();
    const onStorage = (e) => {
      // Déconnexion : le token est stocké sous "token", et l'intercepteur 401 fait
      // un localStorage.clear() qui émet un évènement avec key === null.
      if (e.key === null || (e.key === "token" && !e.newValue)) clearCache();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [fetchClaimAssociatedMissions, clearCache]);

  const openTypeMenu = (e) => setAnchorEl(e.currentTarget);
  const closeTypeMenu = () => setAnchorEl(null);

  const chooseMissionType = (code) => {
    setSelectedMissionTypeCode(code);
    closeTypeMenu();
    setShowInspectorsModal(true);
  };

  const handleInspectorPicked = (ins) => {
    setSelectedInspector(enrichInspector(ins));
  };

  const onConfirmCreate = async () => {
    if (!selectedInspector || !selectedMissionTypeCode) return;
    const assignedExpert = selectedInspector?.associatedParty || "";
    const payload = {
      assignedExpert,
      missionType: selectedMissionTypeCode,
      status: "OPEN",
      initialCarValue: 0,
      currentCarValue: 0,
      ...(claimNumber ? { relatedClaimUid: claimNumber } : {}),
      ...(claimNumber ? { claimNumber } : {}),
    };
    try {
      setCreating(true);
      const created = await dispatch(createInspectionMission(payload));
      if (!created) throw new Error("No mission returned");
      const optimistic = {
        ...created,
        missionDate: created.missionDate || new Date().toISOString(),
      };
      setExpertisesData((prev) => [optimistic, ...(Array.isArray(prev) ? prev : [])]);
      writeCache([optimistic, ...(Array.isArray(readCache()) ? readCache() : [])]);
      ensure(assignedExpert);
      await dispatch(
        updateClaimDaaq({
          claimNumber,
          existingDaaq: currentClaim?.daaq,
          managementType: "CLASSIQUE",
          agency: agencyName,
          addInspector: assignedExpert,
        })
      );
      setShowInspectorsModal(false);
      setSelectedInspector(null);
      setToast({ open: true, severity: "success", msg: "Expert affecté avec succès." });
    } catch (e) {
      const apiMsg =
        e?.response?.data?.errors?.join(" • ") ||
        e?.response?.data?.message ||
        "Une erreur est survenue lors de l'affectation.";
      setToast({ open: true, severity: "error", msg: apiMsg });
    } finally {
      setCreating(false);
    }
  };

  const onAskDelete = (mission) => {
    setDeleteTarget(mission);
    setDeleteOpen(true);
  };

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      // Supprimer côté serveur avant de retirer de l'écran : un retrait optimiste
      // laissait la mission disparue même quand l'API refusait la suppression.
      await apiDeleteMission(deleteTarget);
      const idToRemove = deleteTarget._id || deleteTarget.uid || deleteTarget.code;
      setExpertisesData((prev) =>
        (prev || []).filter((m) => (m._id || m.uid || m.code) !== idToRemove)
      );
      writeCache(
        (readCache() || []).filter((m) => (m._id || m.uid || m.code) !== idToRemove)
      );
      await dispatch(
        updateClaimDaaq({
          claimNumber,
          existingDaaq: currentClaim?.daaq,
          managementType: "CLASSIQUE",
          agency: agencyName,
          removeInspector: deleteTarget?.assignedExpert,
        })
      );
      setToast({ open: true, severity: "success", msg: "Mission supprimée." });
      setDeleteOpen(false);
      setDeleteTarget(null);
    } catch (e) {
      const apiMsg =
        e?.response?.data?.errors?.join(" • ") ||
        e?.response?.data?.message ||
        "Suppression impossible.";
      setToast({ open: true, severity: "error", msg: apiMsg });
    } finally {
      setDeleting(false);
    }
  };




  return (
    <div style={{ padding: "40px", fontFamily: "Poppins, sans-serif" }}>
      <Accordion defaultIndex={[0]} allowMultiple>
        {loading ? ( 
      <>
      {[...Array(3)].map((row, index) => {
        return (
            <AccordionItem className={`${classes.customAccordionItem} ${renderAsSpans ? classes.expanded : ''}`} key={index}>
              <AccordionButton
                  onClick={() => toggleExpansion(index, expandedItems, setExpandedItems)}
                  style={{ height: "47px", border: "none", background: "rgba(249, 249, 249, 1)", padding: "10px", borderRadius: "11px" }}  >
            <AccordionIcon size="30px" style={{ backgroundColor: "rgba(240, 243, 199, 1)", width: "25px", height: "25px", borderRadius: "5px", color: "rgba(187, 200, 8, 1)" }} />
            <Skeleton  size="30px" animation="wave" width={160} height={30}/>
          </AccordionButton>
          <AccordionPanel style={{ width: "100%", background: 'rgba(255, 255, 255, 1)', padding: '30px' , borderRadius: "11px" }}>
              <Grid container style={{justifyContent: 'start' , marginBottom :'10px'}}  spacing={2} >
                  <Grid item  > 
                      <div>
                      <div className={getLabelClass(renderAsSpans, classes)} style={{marginRight: "40px"}}>N° Mission</div>
                        <Skeleton animation="wave" width={241} height={50}/>
                      </div>
                  </Grid>
                  <Grid item  > 
                      <div>
                      <div className={getLabelClass(renderAsSpans, classes)} style={{marginRight: "40px"}}>Date mission</div>
                        <Skeleton animation="wave" width={241} height={50}/>
                      </div>
                  </Grid>
                  <Grid item  > 
                      <div>
                      <div className={getLabelClass(renderAsSpans, classes)} style={{marginRight: "40px"}}>Expert désigné</div>
                        <Skeleton animation="wave" width={241} height={50}/>
                      </div>
                  </Grid>
                  <Grid item  > 
                      <div>
                      <div className={getLabelClass(renderAsSpans, classes)} style={{marginRight: "40px"}}>Type mission</div>
                        <Skeleton animation="wave" width={241} height={50}/>
                      </div>
                  </Grid>
                   
          </Grid>
            
          </AccordionPanel>
        </AccordionItem>
        )})}
        </>
        ) : (
          <>
            {expertisesData.length > 0 ? (
              <>
                {hasAuthorities("CREATE_INSPECTION_MISSION") && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                  <Button
                    onClick={openTypeMenu}
                    startIcon={<AddIcon />}
                    sx={{
                      textTransform: "none",
                      borderRadius: 1.5,
                      px: 2,
                      py: 1,
                      bgcolor: BRAND.blue,
                      color: "#fff",
                      fontFamily: "Poppins",
                      "&:hover": { bgcolor: BRAND.blue },
                    }}
                  >
                    Ajouter
                  </Button>
                </Box>
                )}
                {expertisesData.map((expertises, index) => (
                  <AccordionItem
                    className={`${classes.customAccordionItem} ${renderAsSpans ? classes.expanded : ""}`}
                    key={expertises?._id || expertises?.uid || expertises?.code || index}
                  >
                    <AccordionButton
                      onClick={() => toggleExpansion(index, expandedItems, setExpandedItems)}
                      style={{ height: "47px", border: "none", background: "rgba(249, 249, 249, 1)", padding: "10px", borderRadius: "11px" }}
                    >
                      <AccordionIcon size="30px" style={{ backgroundColor: BRAND.greenLight, width: "25px", height: "25px", borderRadius: "5px", color: BRAND.green }} />
                      <Box as="span" textAlign="left" marginLeft="20px" style={{ color: "rgba(0, 34, 38, 1)", fontSize: "15px", fontWeight: 500, lineHeight: "21px", fontFamily: "Poppins" }}>
                        {expertises?.code}
                      </Box>
                      <div style={{ justifyContent: "space-between", display: "flex", width: "100%", alignItems: "center" }}>
                        <div style={{ marginLeft: "10px", height: "26px", borderRadius: "51px", background: BRAND.greenLight, textAlign: "center", padding: "5px 20px", fontFamily: "Poppins", fontSize: "10.69px", fontWeight: 400, lineHeight: "16.03px", color: BRAND.green }}>
                          {traduireTypeDeMission(expertises?.missionType)}
                        </div>
                        {hasAuthorities("DELETE_INSPECTION_MISSION") && (
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<DeleteOutline />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onAskDelete(expertises);
                          }}
                          sx={{ textTransform: "none", fontFamily: "Poppins" }}
                        >
                          Supprimer
                        </Button>
                        )}
                      </div>
                    </AccordionButton>
            <AccordionPanel style={{ width: "100%", background: 'rgba(255, 255, 255, 1)', padding: '30px' , borderRadius: "11px" }}>
              <Grid container style={{justifyContent: 'start' , marginBottom :'10px'}}  spacing={2} >
                <Grid item  > 
                  <div className={getLabelClass(renderAsSpans, classes)} style={{marginRight: "40px"}}>N° Mission</div>
                  {/*<input disabled className={classes.styleInput} type='text' name="numMission" value={expertises?.code  || "N/A"} /> */}
                  {renderInputOrSpan(renderAsSpans, expertises?.code || "N/A", classes)}
                </Grid>
                <Grid item  > 
                  <div className={getLabelClass(renderAsSpans, classes)} style={{marginRight: "40px"}}>Date mission</div>
                  {/*<input disabled className={classes.styleInput} type='text' name="dateMission" value={getDateFromString(expertises?.missionDate?.toString())}/>*/}
                  {renderInputOrSpan(renderAsSpans, getDateFromString(expertises?.missionDate?.toString()) || "N/A", classes)}
                </Grid>
                <Grid item>
                  <div className={getLabelClass(renderAsSpans, classes)} style={{ marginRight: "40px" }}>Expert désigné</div>
                  {renderInputOrSpan(
                    renderAsSpans,
                    names[expertises?.assignedExpert] || expertises?.assignedExpert || "N/A",
                    classes
                  )}
                </Grid>
                <Grid item>
                  <div className={getLabelClass(renderAsSpans, classes)} style={{marginRight: "40px"}}>Type mission</div>
                  {/*<input disabled className={classes.styleInput} type="text"  name="typeMission"  value ={expertises?.missionType || "N/A"}/>*/}
                  {renderInputOrSpan(renderAsSpans, traduireTypeDeMission(expertises?.missionType) || "N/A", classes)}
                </Grid>
              </Grid>
            </AccordionPanel>
          </AccordionItem>
         
        ))
      }
    </>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, width: "100%", mb: 2 }}>
                <Box style={{ fontFamily: "Poppins", fontWeight: 400, fontSize: 15, lineHeight: "22.5px", color: "rgba(70, 78, 95, 1)" }}>
                  Aucune mission d&apos;expertise pour ce sinistre
                </Box>
                {hasAuthorities("CREATE_INSPECTION_MISSION") && (
                <Button
                  onClick={openTypeMenu}
                  startIcon={<AddIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: 1.5,
                    px: 2,
                    py: 1,
                    bgcolor: BRAND.blue,
                    color: "#fff",
                    fontFamily: "Poppins",
                    "&:hover": { bgcolor: BRAND.blue },
                  }}
                >
                  Ajouter
                </Button>
                )}
              </Box>
            )}
          </>
        )}
      </Accordion>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeTypeMenu}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 8px 24px rgba(0,0,0,0.12), 0px 2px 8px rgba(0,0,0,0.06)",
            minWidth: 360,
            p: 0.5,
          },
        }}
      >
        {MISSION_TYPES.map((t) => (
          <MenuItem
            key={t.code}
            onClick={() => chooseMissionType(t.code)}
            sx={{
              borderRadius: 1.5,
              "&:hover": { backgroundColor: BRAND.modalBg },
              fontFamily: "Poppins",
              fontSize: 14,
            }}
          >
            {t.label}
          </MenuItem>
        ))}
      </Menu>

      <Dialog
        open={showInspectorsModal}
        onClose={() => {
          setShowInspectorsModal(false);
          setSelectedInspector(null);
        }}
        fullWidth
        maxWidth="lg"
        sx={{ "*": { fontFamily: "Poppins, sans-serif" } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 500,
            pr: 6,
            color: "#3F4254",
            bgcolor: "#ffff",
            borderBottom: "1px solid #e5e7eb",
            position: "relative",
          }}
        >
          Recherche Expert
          <IconButton
            aria-label="close"
            onClick={() => {
              setShowInspectorsModal(false);
              setSelectedInspector(null);
            }}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: BRAND.modalBg }}>
          <ExpertSelector
            designatedExperts={handleInspectorPicked}
            designatedRepairerPartyUid={designatedRepairerPartyUid}
          />
        </DialogContent>
        <DialogActions
          sx={{
            bgcolor: BRAND.modalBg,
            p: 2,
            borderTop: "1px solid #e5e7eb",
          }}
        >
          <Button
            onClick={() => {
              setShowInspectorsModal(false);
              setSelectedInspector(null);
            }}
            sx={{ textTransform: "none", color: "#EF4444", bgcolor: "#FFE5E5" }}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={onConfirmCreate}
            disabled={!selectedInspector || !selectedMissionTypeCode || creating}
            sx={{
              textTransform: "none",
              bgcolor: BRAND.blue,
              fontFamily: "Poppins",
              "&:hover": { bgcolor: BRAND.blue },
            }}
          >
            {creating ? "Validation..." : "Valider"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{ "*": { fontFamily: "Poppins, sans-serif" } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Supprimer la mission ?</DialogTitle>
        <DialogContent dividers>
          <Box component="p" sx={{ fontFamily: "Poppins", fontSize: 14 }}>
            Cette action est irréversible.
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteOpen(false)}
            disabled={deleting}
            sx={{ textTransform: "none", fontFamily: "Poppins" }}
          >
            Annuler
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={onConfirmDelete}
            disabled={deleting}
            sx={{ textTransform: "none", fontFamily: "Poppins" }}
          >
            {deleting ? "Suppression..." : "Supprimer"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default ExpertisesSection;